/* eslint-disable max-lines-per-function */
import { type ReactNode, useCallback } from 'react';

import {
  type ContextSelectionItem,
  ContextSelectionType,
  useContextSelection,
} from '@/components/chat/ContextSelectionContext';
import { ChatContextMenuItem } from '@/components/chat/chat-context-menu/ChatContextMenuItem';
import { ChatContextMenuItemSkeleton } from '@/components/chat/chat-context-menu/ChatContextMenuItemSkeleton';
import { ChatContextMenuSeparator } from '@/components/chat/chat-context-menu/ChatContextMenuSeparator';
import { ExperimentItem } from '@/components/chat/chat-context-menu/hooks/useExperiments';
import { ProductItem } from '@/components/chat/chat-context-menu/hooks/useProducts';
import { ToolItem } from '@/components/chat/chat-context-menu/hooks/useTools';
import { ToolVersionSelector } from '@/components/chat/chat-context-menu/tools/ToolVersionSelector';
import { useToolVersionPickerFlow } from '@/components/chat/chat-context-menu/useToolVersionPickerFlow';

const isToolItem = (
  item: ExperimentItem | ProductItem | ToolItem
): item is ToolItem => 'versionsCount' in item;

interface ChatContextMenuAllSectionProps {
  icon: ReactNode;
  title: string;
  type: ContextSelectionType;
  loading: boolean;
  items: ExperimentItem[] | ProductItem[] | ToolItem[];
}

export const ChatContextMenuAllSection = ({
  icon,
  title,
  type,
  loading,
  items,
}: ChatContextMenuAllSectionProps) => {
  const { addContextSelection, runOnSelectItem } = useContextSelection();

  const handleCommit = useCallback(
    (selection: ContextSelectionItem) => {
      addContextSelection(selection);
      runOnSelectItem(selection);
    },
    [addContextSelection, runOnSelectItem]
  );

  const {
    pendingToolItem,
    isSheetOpen,
    requestToolSelection,
    confirmVersionSelection,
    handleSheetOpenChange,
    closeSheet,
  } = useToolVersionPickerFlow({ onCommit: handleCommit });

  const handleNonToolClick = useCallback(
    ({
      id,
      itemTitle,
      updatedAt,
    }: {
      id: string;
      itemTitle: string;
      updatedAt: string | null;
    }) => {
      const nextSelection: ContextSelectionItem = {
        id: id,
        title: itemTitle,
        type: type,
        lastEditLabel: updatedAt ?? '',
      };

      handleCommit(nextSelection);
    },
    [type, handleCommit]
  );

  const handleToolRowClick = useCallback(
    (toolItem: ToolItem) => {
      requestToolSelection(toolItem, ContextSelectionType.Tool);
    },
    [requestToolSelection]
  );

  return (
    <>
      <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold text-foreground">
        {icon}
        {title} {!loading && `(${items.length})`}
      </div>
      <ChatContextMenuSeparator />
      {loading &&
        [1, 2].map((item) => <ChatContextMenuItemSkeleton key={item} />)}
      {items.map((item: ExperimentItem | ProductItem | ToolItem) => {
        if (type === ContextSelectionType.Tool && isToolItem(item)) {
          return (
            <ChatContextMenuItem
              key={item.id}
              onClick={() => handleToolRowClick(item)}
            >
              {item.name}
            </ChatContextMenuItem>
          );
        }

        return (
          <ChatContextMenuItem
            key={item.id}
            onClick={() =>
              handleNonToolClick({
                id: item.id,
                itemTitle: item.name,
                updatedAt: item.updatedAt,
              })
            }
          >
            {item.name}
          </ChatContextMenuItem>
        );
      })}
      {pendingToolItem && (
        <ToolVersionSelector
          open={isSheetOpen}
          onOpenChange={handleSheetOpenChange}
          onClose={closeSheet}
          onConfirm={confirmVersionSelection}
          versions={pendingToolItem.versions.map((version) => ({
            id: version.id,
            name: version.name,
            editedAt: version.editedAt ?? '',
          }))}
          toolName={pendingToolItem.name}
        />
      )}
    </>
  );
};
